<template>
  <div
    class="fixed right-0 bottom-26 z-(--z-system) flex items-end transition-transform duration-300"
    :class="!shown && 'translate-x-full'"
    @mouseleave="listShown = false"
  >
    <div
      v-if="listShown && uploads.length"
      class="absolute bottom-0 right-full mr-2 flex w-72 flex-col gap-3 rounded-lg border border-line bg-popover-solid p-3 text-text shadow-lg"
    >
      <div v-for="upload in uploads" :key="upload.id" class="flex min-w-0 flex-col gap-1">
        <div class="flex justify-between gap-2 text-label">
          <span class="truncate">{{ upload.name }}</span>
          <span class="shrink-0 text-text-muted tabular-nums">{{ fileSize(upload.total) }}</span>
        </div>
        <div class="h-1 overflow-hidden rounded-full bg-line">
          <i class="block h-full" :class="barColor(upload)" :style="{ width: `${percent(upload)}%` }"></i>
        </div>
        <div class="flex flex-wrap items-center gap-2 text-unit" :class="upload.state === 'failed' ? 'text-red' : 'text-text-muted'">
          {{ statusText(upload) }}
          <template v-if="upload.state === 'failed'">
            <SimpleButton size="xs" :text="$t('uploads.retry')" @click="retryUpload(upload)"/>
            <SimpleButton size="xs" variant="ghost" :text="$t('uploads.remove')" @click="dropUpload(upload)"/>
          </template>
        </div>
      </div>
    </div>
    <button
      class="flex items-center gap-2 rounded-l-[2.5rem] border-0 p-2.5 transition-opacity duration-300
             [&_.loader]:m-0 [&_.loader]:size-2.5 [&_.loader]:border-transparent [&_.loader]:border-l-on-accent"
      :class="failedCount ? 'bg-red text-on-red' : 'bg-accent text-on-accent opacity-50 hover:opacity-100'"
      :title="uploads.length ? `${uploadsProgress}%` : $t('system.syncing')"
      @mouseenter="listShown = true"
      @click="listShown = !listShown"
    >
      <span v-if="failedCount" class="text-unit font-semibold whitespace-nowrap">
        {{ $t('uploads.not_uploaded', { count: failedCount }) }}
      </span>
      <template v-else-if="uploads.length">
        <span class="block h-1 w-16 overflow-hidden rounded-2xl bg-on-accent/20">
          <i class="block h-full bg-on-accent transition-[width] duration-200 ease-linear" :style="{ width: `${uploadsProgress}%` }"></i>
        </span>
        <span class="text-unit font-semibold tabular-nums">{{ uploadsProgress }}%</span>
      </template>
      <Loader v-else/>
    </button>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import humanize from 'humanize';
import {
  uploads, uploadsProgress, retryUpload, dropUpload, MAX_UPLOAD_RETRIES,
} from '@/utils/uploadQueue';

const store = useStore();
const { t } = useI18n();

const listShown = ref(false);
const syncing = computed(() => store.state.system.syncing);
const shown = computed(() => syncing.value || uploads.length > 0);
const failedCount = computed(() => uploads.filter(u => u.state === 'failed').length);

const fileSize = bytes => humanize.filesize(bytes, 1024, 1);
const percent = upload => Math.floor((upload.loaded / upload.total) * 100);
const barColor = upload => ({
  failed: 'bg-red',
  done: 'bg-green',
  retry: 'bg-amber',
  offline: 'bg-amber',
}[upload.state] || 'bg-accent');

function statusText(upload) {
  switch (upload.state) {
    case 'uploading': return `${fileSize(upload.loaded)} / ${fileSize(upload.total)}`;
    case 'retry': return t('uploads.retry_attempt', { attempt: upload.retries, max: MAX_UPLOAD_RETRIES });
    case 'announcing': return t('uploads.queued');
    case 'uploaded': return t('uploads.saving');
    case 'failed': return t(`uploads.errors.${upload.error.reason}`, { limit: upload.error.limit && fileSize(upload.error.limit) });
    default: return t(`uploads.${upload.state}`);
  }
}
</script>
